import { Group, Intersection, Matrix4, Ray, Raycaster, Vector3 } from 'three';

import { Point2D, Polygon2D } from '@paragon/geometry-library';

import { convertFromDesignToLayoutCoordinates } from '../../library/layout-geometry/coordinates';
import { getCameraClickRadius } from '../ThreeJsSceneActions';

const MAXIMUM_RAYCAST_INFLATION = 1;
const RAYCAST_INFLATION_MULTIPLIER = 0.5;

const inverseMatrix = new Matrix4();
const worldSpaceRay = new Ray();
const localSpaceRay = new Ray();
const localSpaceTarget = new Vector3();
const worldSpaceTarget = new Vector3();
let layoutPosition = new Point2D(0, 0);

export const getLayoutPolygonRaycastInflation = () =>
  Math.min(getCameraClickRadius() * RAYCAST_INFLATION_MULTIPLIER, MAXIMUM_RAYCAST_INFLATION);

/**
 * Truss and bearing envelope visuals are drawn in 2D with 1px lines. This bounds-based raycaster registers any click
 *   within the stored polygon or the zoom-scaled inflation distance outside the polygon as a click on the visual,
 *   making it much easier to select them. It should also be faster.
 *
 * To use, add a `{ raycastPolygon: Polygon2D, raycastElevation: number }` property to a visual's `userData` and set
 *   `raycast={raycastLayoutCoordinatesPolygon}`. Make sure to disable raycasting on the lines themselves for
 *    performance.
 */
export function raycastLayoutCoordinatesPolygon(this: Group, raycaster: Raycaster, intersects: Intersection[]) {
  const raycastPolygon = this.userData.raycastPolygon as Polygon2D | undefined;
  const elevation = this.userData.raycastElevation as number | undefined;
  if (raycastPolygon == null || elevation == null) return;

  // if this is the first polygon for this ray, calculate data that can be reused for subsequent polygons
  if (!raycaster.ray.equals(worldSpaceRay)) {
    inverseMatrix.copy(this.matrixWorld).invert();

    worldSpaceRay.copy(raycaster.ray);
    localSpaceRay.copy(raycaster.ray).applyMatrix4(inverseMatrix);
  }

  if (localSpaceRay.direction.y === 0) return; // ray is parallel to the plane; no intersection possible
  const distanceAlongRay = (elevation - localSpaceRay.origin.y) / localSpaceRay.direction.y;
  localSpaceTarget.copy(localSpaceRay.origin).addScaledVector(localSpaceRay.direction, distanceAlongRay);
  layoutPosition = convertFromDesignToLayoutCoordinates(localSpaceTarget).projectTo2D();

  if (!raycastPolygon.containsPointWithinDistance(layoutPosition, getLayoutPolygonRaycastInflation())) return;

  worldSpaceTarget.copy(localSpaceTarget).applyMatrix4(this.matrixWorld);

  intersects.push({
    object: this,
    point: worldSpaceTarget.clone(),
    distance: worldSpaceTarget.distanceTo(worldSpaceRay.origin),
  });
}
